import { Box, Button, IconButton, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import strings from "global/constants/StringConstants";
import { fillCopy } from "helpers/methods";
import { campaignOf, PromoPhase, toTextRuns } from "helpers/promotionHelpers";
import { promoThemes, promoTokens } from "./DashboardPromotions.tokens";

interface Props {
  phase: Exclude<PromoPhase, "none">;
  onViewOffer: () => void;
  onNotify: () => void;
  onDismiss: () => void;
}

const t = promoTokens.strip;

/**
 * One-line promotion under the dashboard header. Every phase gets the same
 * strip. The teaser has no offer yet, so its call to action is "Notify me",
 * which opens the Mailzzy signup popup; every sale phase leads to the offer.
 */
const DashboardPromoStrip = ({
  phase,
  onViewOffer,
  onNotify,
  onDismiss,
}: Props) => {
  const campaign = campaignOf(phase) ?? "bf";
  const theme = promoThemes[campaign];
  const copy = strings.promotions.strip[phase];
  const runs = toTextRuns(copy.text);
  const hasOffer = phase !== "teaser";

  return (
    <Box
      role="region"
      aria-label={fillCopy(strings.promotions.stripAria, {
        badge: copy.badge.toLowerCase(),
      })}
      sx={{
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: t.gap,
        padding: t.padding,
        borderRadius: t.radius,
        background: theme.bg,
        color: theme.fg,
        boxShadow: `0 8px 24px ${theme.glow}`,
      }}
    >
      {/* Signature flourish in the corner — decoration only. */}
      <Box
        component="svg"
        aria-hidden="true"
        viewBox="0 0 220 60"
        fill="none"
        sx={{
          position: "absolute",
          right: "-30px",
          bottom: "-14px",
          width: 200,
          height: 56,
          opacity: theme.decoOpacity,
          pointerEvents: "none",
        }}
      >
        <path
          d="M6 42c18-26 30-30 34-18s-12 26-4 24 22-36 34-36-6 34 4 32 18-22 28-22 0 18 10 18 22-12 34-14 26 4 64 2"
          stroke={theme.decoStroke}
          strokeWidth="3"
          strokeLinecap="round"
        />
      </Box>

      <Box
        aria-hidden="true"
        sx={{
          position: "relative",
          width: t.tileSize,
          height: t.tileSize,
          borderRadius: t.tileRadius,
          backgroundColor: theme.tileBg,
          color: theme.accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
          sx={{ width: t.iconSize, height: t.iconSize }}
        >
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M14 3v5h5" />
          <path d="M8 16c1.2-1.6 2-1.6 2.4-.6s1.2.9 2-.2 1.6-.9 2.2.3" />
        </Box>
      </Box>

      <Box
        sx={{
          position: "relative",
          flex: "1 1 300px",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "4px",
        }}
      >
        <Typography
          component="span"
          sx={{
            alignSelf: "flex-start",
            fontSize: t.badgeFontSize,
            fontWeight: 800,
            letterSpacing: t.badgeLetterSpacing,
            lineHeight: 1.2,
            color: theme.badgeFg,
            backgroundColor: theme.badgeBg,
            borderRadius: "999px",
            padding: t.badgePadding,
          }}
        >
          {copy.badge}
        </Typography>
        <Typography
          sx={{
            fontSize: t.textFontSize,
            fontWeight: 600,
            lineHeight: 1.5,
            color: theme.fg,
          }}
        >
          {runs.map((run, index) => (
            <Box
              key={index}
              component="span"
              sx={
                run.bold
                  ? { fontWeight: 800, color: theme.boldText }
                  : undefined
              }
            >
              {run.text}
            </Box>
          ))}
        </Typography>
      </Box>

      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          flexShrink: 0,
          ml: "auto",
        }}
      >
        <Button
          variant="contained"
          onClick={hasOffer ? onViewOffer : onNotify}
          sx={{
            height: t.ctaHeight,
            px: "16px",
            borderRadius: t.ctaRadius,
            fontSize: t.ctaFontSize,
            fontWeight: 800,
            whiteSpace: "nowrap",
            boxShadow: "none",
            // One specificity step up: the theme's contained fill is set with
            // the `background` shorthand and beats a plain `bgcolor`.
            "&.MuiButton-root": {
              background: theme.ctaBg,
              color: theme.ctaFg,
              "&:hover": { background: theme.ctaBg, opacity: 0.92 },
            },
          }}
        >
          {`${hasOffer ? strings.promotions.viewOffer : strings.promotions.notifyMe} →`}
        </Button>
        <IconButton
          aria-label={strings.promotions.dismissStrip}
          onClick={onDismiss}
          sx={{
            width: t.dismissSize,
            height: t.dismissSize,
            borderRadius: t.dismissRadius,
            color: theme.fg,
            opacity: 0.85,
            "&:hover": { backgroundColor: theme.tileBg },
            // Not the app's brand-red `focusRing`: it disappears on the
            // Year-End red/pink gradient (WCAG 2.4.7), so the ring takes the
            // strip's own foreground, which clears 3:1 on both surfaces.
            "&:focus-visible": {
              outline: `2px solid ${theme.fg}`,
              outlineOffset: "2px",
            },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: t.dismissGlyph }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default DashboardPromoStrip;
